import React, { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { StyleProp, ViewStyle } from 'react-native';
import { MBViewPadding } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBModal } from '@shared/components/modal';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { ErrorBoundary } from '@shared/scenes/error';
import { useSelector } from '@shared/store';
import { useIsDesktop } from '@shared/util/hooks';
import styles from './modalOrMobile.module.css';

export interface MBModalOrMobileProps {
  /*
        Is the modal/content visible? Default is true.
    */
  visible?: boolean;

  /*
        Title to be used for the modal on desktop
    */
  title?: string;

  /*
        Background color of the wrapping view.

        Note: on desktop this is the only good way to set
        background color since the modal wrapper has padding.
        So child views wouldn't be able to set the background
        color for the whole modal.
    */
  backgroundColor?: string;

  /*
        Header for mobile. This can eg be a nav bar with back buttons
        or other web specific code that we currently don't want to
        port to shared components.
    */
  headerOnMobile?: ReactNode;

  /*
        Padding to add to the children on mobile.
    */
  paddingOnMobile?: number;

  /*
        Padding of the web modal content. Default is "normal".
    */
  paddingOnWeb?: MBViewPadding;

  /*
        Minimum height of the desktop modal. This is used for instance
        to prevent the modal from being super small while content inside
        it is loading (and only a loading indicator is visible).
        Default is 320.
    */
  desktopMinModalHeight?: number | string;

  /*
        Fixed height of the desktop modal. Default is 480.
        Note: we don't use min width since we want the modal width to
        be consistent and not change depending on content.
    */
  desktopModalWidth?: number | string;
  desktopModalHeight?: number | string;

  desktopMaxWidth?: number | string;
  desktopMinWidth?: number | string;

  /*
        Optional React component to put in the upper left/right corner
        of the modal, replacing the back/close button if visible.
    */
  desktopModalRightButton?: ReactNode;
  desktopModalLeftButton?: ReactNode;

  /*
        Callback for when the modal close button or backdrop is pressed
    */
  onClose?: () => void;
  shouldCloseOnOverlayClick?: boolean;

  /*
        Callback for when the modal back button is pressed.
        Note: since we currently use headerOnMobile, we don't
        use onBack for mobile specific navigation. That is
        currently supplied when using this component.

        If this is not set, no back button will be rendered
    */
  onBack?: () => void;

  // From MBModalProps
  webOverflow?: boolean;
  desktopFooter?: ReactNode;
  isCloseButtonHidden?: boolean;
  webOverlayStyle?: React.CSSProperties;

  children?: ReactNode;
  testID?: string;
}

export function MBModalOrMobile(props: MBModalOrMobileProps) {
  const isDesktop = useIsDesktop();
  if (isDesktop) {
    return <DesktopModal {...props} />;
  } else {
    return <MobileModal {...props} />;
  }
}

function DesktopModal({
  title,
  backgroundColor,
  children,
  onClose,
  onBack,
  shouldCloseOnOverlayClick,
  webOverflow,
  desktopModalRightButton,
  desktopModalLeftButton,
  desktopFooter,
  paddingOnWeb,
  desktopModalWidth = 480,
  desktopMinModalHeight = 320,
  desktopMinWidth,
  desktopMaxWidth,
  desktopModalHeight,
  visible = true,
  isCloseButtonHidden = !!onBack,
  webOverlayStyle = {},
  testID,
}: MBModalOrMobileProps) {
  const viewportWidth = useSelector(state => state.appState.viewportWidth);
  const responsiveForms = useResponsiveFormContext();
  const [animateModalWidth, setAnimateModalWidth] = useState(false);

  useEffect(() => {
    // don't animate width when first opening the card
    const timer = setTimeout(() => {
      setAnimateModalWidth(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const responsiveContainerStyle: StyleProp<ViewStyle> = { maxWidth: '100vw', overflow: 'hidden' };
  const webContainerStyle = responsiveForms ? responsiveContainerStyle : undefined;

  return (
    <MBModal
      visible={visible}
      title={title}
      backgroundColor={backgroundColor}
      onBack={onBack}
      onClose={onClose}
      isCloseButtonHidden={isCloseButtonHidden}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      padding={paddingOnWeb}
      leftButton={desktopModalLeftButton}
      rightButton={desktopModalRightButton}
      webOverflow={webOverflow}
      webFooter={desktopFooter}
      webOverlayStyle={webOverlayStyle}
      webContainerStyle={webContainerStyle}
      testID={testID}
    >
      <View
        style={{
          width: desktopModalWidth,
          height: desktopModalHeight,
          minWidth: desktopMinWidth,
          maxWidth: desktopMaxWidth ?? viewportWidth,
          minHeight: desktopMinModalHeight,
          transition: animateModalWidth ? 'max-width 0.2s ease-in-out' : undefined,
        }}
      >
        <ErrorBoundary>{children}</ErrorBoundary>
      </View>
    </MBModal>
  );
}

function MobileModal({
  backgroundColor,
  webOverflow,
  paddingOnMobile,
  headerOnMobile,
  testID,
  children,
}: MBModalOrMobileProps) {
  return createPortal(
    <View
      padding={paddingOnMobile}
      className={[styles.mobileModal, webOverflow && styles.webOverflow]}
      style={{ backgroundColor }}
      testID={testID}
    >
      {headerOnMobile && <View className={styles.header}>{headerOnMobile}</View>}
      <ErrorBoundary>{children}</ErrorBoundary>
    </View>,
    document.body,
  );
}
